import dbStorageService from '../dbStorageService';
import cacheHelper from './cacheHelper';

/**
 * Helper for managing circle membership operations
 */
const membershipHelper = {
    /**
     * Adds a member to a circle
     * @param {string} circleId - UUID of the circle to add member to
     * @param {Object} memberData - Data for the member to add
     * @returns {Promise<Object>} Updated circle record
     */
    async addMember(circleId, memberData) {
        try {
            const circles = await dbStorageService.getRecordsByField('Circle', 'uuid', circleId);
            if (!circles.length) {
                throw new Error('Circle not found');
            }
            
            const circle = circles[0];
            
            // Parse existing members
            let members = [];
            try {
                members = JSON.parse(circle.members);
            } catch (e) {
                console.error('Failed to parse members:', e);
            }
            
            // Ensure members is an array
            if (!Array.isArray(members)) {
                members = [];
            }
            
            // Check if this member already exists
            const existingMemberIndex = members.findIndex(m => m.userId === memberData.userId);
            if (existingMemberIndex !== -1) {
                // Update existing member
                members[existingMemberIndex] = {
                    ...members[existingMemberIndex],
                    ...memberData
                };
            } else {
                // Add new member
                members.push(memberData);
            }
            
            // Update circle with new members array
            const updatedCircle = {
                ...circle,
                members: JSON.stringify(members),
                lastModified: new Date().toISOString()
            };
            
            // Mark as user-initiated to trigger sync backoff
            await dbStorageService.upsertRecords('Circle', [updatedCircle], null, true);
            
            cacheHelper.clearCache();
            return updatedCircle;
        } catch (error) {
            console.error('Failed to add member to circle:', error);
            throw error;
        }
    },

    /**
     * Removes a member from a circle
     * @param {string} circleId - UUID of the circle
     * @param {number|string} userId - ID of the user to remove
     * @returns {Promise<Object>} Updated circle record
     */
    async removeMember(circleId, userId) {
        try {
            const circles = await dbStorageService.getRecordsByField('Circle', 'uuid', circleId);
            if (!circles.length) {
                throw new Error('Circle not found');
            }
            
            const circle = circles[0];
            
            // Parse existing members
            let members = [];
            try {
                members = JSON.parse(circle.members);
            } catch (e) {
                console.error('Failed to parse members:', e);
            }
            
            // Ensure members is an array
            if (!Array.isArray(members)) {
                throw new Error('Invalid members data for circle');
            }
            
            // Remove the specified user
            const updatedMembers = members.filter(member => member.userId !== userId);
            
            // Update circle with new members array
            const updatedCircle = {
                ...circle,
                members: JSON.stringify(updatedMembers),
                lastModified: new Date().toISOString()
            };
            
            // Mark as user-initiated to trigger sync backoff
            await dbStorageService.upsertRecords('Circle', [updatedCircle], null, true);
            
            cacheHelper.clearCache();
            return updatedCircle;
        } catch (error) {
            console.error('Failed to remove member from circle:', error);
            throw error;
        }
    },

    /**
     * Updates a member's role in a circle
     * @param {string} circleId - UUID of the circle
     * @param {number|string} userId - ID of the user
     * @param {number} newRole - New role value
     * @returns {Promise<Object>} Updated circle record
     */
    async updateMemberRole(circleId, userId, newRole) {
        try {
            const circles = await dbStorageService.getRecordsByField('Circle', 'uuid', circleId);
            if (!circles.length) {
                throw new Error('Circle not found');
            }
            
            const circle = circles[0];
            
            // Parse existing members
            let members = [];
            try {
                members = JSON.parse(circle.members);
            } catch (e) {
                console.error('Failed to parse members:', e);
                throw new Error('Invalid members data for circle');
            }
            
            // Ensure members is an array
            if (!Array.isArray(members)) {
                throw new Error('Invalid members data for circle');
            }
            
            // Find the specified user
            const memberIndex = members.findIndex(member => member.userId === userId);
            if (memberIndex === -1) {
                throw new Error('Member not found in circle');
            }
            
            // Update the role
            members[memberIndex].role = newRole;
            
            // Update circle with new members array
            const updatedCircle = {
                ...circle,
                members: JSON.stringify(members),
                lastModified: new Date().toISOString()
            };
            
            // Mark as user-initiated to trigger sync backoff
            await dbStorageService.upsertRecords('Circle', [updatedCircle], null, true);
            
            cacheHelper.clearCache();
            return updatedCircle;
        } catch (error) {
            console.error('Failed to update member role:', error);
            throw error;
        }
    },

    /**
     * Gets circles that the current user belongs to
     * @param {number|string} userId - ID of the user
     * @returns {Promise<Array>} List of circles with user's role
     */
    async getCurrentUserCircles(userId) {
        try {
            const circles = await dbStorageService.getAllRecords('Circle');
            
            const userCircles = circles
                .filter(circle => !circle.isDeleted)
                .filter(circle => {
                    try {
                        const members = JSON.parse(circle.members || '[]');
                        return members.some(m => m.userId === userId);
                    } catch (e) {
                        console.error(`Error parsing members for circle ${circle.uuid}:`, e);
                        return false;
                    }
                })
                .map(circle => {
                    try {
                        const members = JSON.parse(circle.members);
                        const userMember = members.find(m => m.userId === userId);
                        return {
                            uuid: circle.uuid,
                            name: circle.name,
                            role: userMember?.role
                        };
                    } catch (e) {
                        console.error(`Error getting role for circle ${circle.uuid}:`, e);
                        return {
                            uuid: circle.uuid,
                            name: circle.name
                        };
                    }
                });
                
            return userCircles;
        } catch (error) {
            console.error(`Error getting circles for current user ${userId}:`, error);
            throw new Error(`Failed to get user circles: ${error.message}`);
        }
    },

    /**
     * Check if a user has edit permission for another user
     * @param {number|string} currentUserId - The ID of the current user
     * @param {number|string} targetUserId - The ID of the user whose data would be edited
     * @returns {Promise<boolean>} Whether the current user has edit permission
     */
    async hasEditPermissionForUser(currentUserId, targetUserId) {
        // Users can always edit their own data
        if (currentUserId === targetUserId) {
            return true;
        }
        
        try {
            // Get members from the same circles
            const circles = await dbStorageService.getAllRecords('Circle');
            
            // Find circles containing both users
            const sharedCircles = circles.filter(circle => {
                try {
                    const members = JSON.parse(circle.members || '[]');
                    const hasCurrentUser = members.some(m => m.userId === currentUserId);
                    const hasTargetUser = members.some(m => m.userId === targetUserId);
                    return hasCurrentUser && hasTargetUser;
                } catch (e) {
                    return false;
                }
            });
            
            // Check if current user has sufficient role in any shared circle
            for (const circle of sharedCircles) {
                try {
                    const members = JSON.parse(circle.members);
                    const currentUserMember = members.find(m => m.userId === currentUserId);
                    
                    // Check if current user has sufficient role (>= 30)
                    if (currentUserMember && currentUserMember.role >= 30) {
                        return true;
                    }
                } catch (e) {
                    continue;
                }
            }
            
            return false;
        } catch (error) {
            console.error('[CircleService] Error checking edit permission:', error);
            return false;
        }
    },

    /**
     * Gets the highest role a user has across all their circles
     * @param {number|string} userId - The ID of the user to check
     * @returns {Promise<number>} The highest role value (0 if not in any circles)
     */
    async getCurrentUserHighestRole(userId) {
        if (!userId) return 0;
        
        try {
            const circles = await dbStorageService.getAllRecords('Circle');
            let highestRole = 0;
            
            for (const circle of circles) {
                if (circle.isDeleted) continue;
                
                try {
                    const members = JSON.parse(circle.members || '[]');
                    const userMember = members.find(m => m.userId === userId);
                    
                    if (userMember && userMember.role > highestRole) {
                        highestRole = userMember.role;
                    }
                } catch (e) {
                    console.error(`Error parsing members for circle ${circle.uuid}:`, e);
                }
            }
            
            return highestRole;
        } catch (error) {
            console.error(`[CircleService] Error getting highest role for user ${userId}:`, error);
            return 0; // Default to no role on error
        }
    }
};

export default membershipHelper; 